import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["folder", "empty", "sidebar", "toggleButton", "folderContent", "folderIcon", "emptyToggle", "emptyIcon", "emptyText", "sidebarItem", "categoryTitle"]
  static values = {
    sidebarLevel: { type: Number, default: 0 },
    hideEmpty: { type: Boolean, default: false },
    hides: { type: Array, default: [] },
    scrollKey: { type: String, default: "" }
  }

  connect() {
    this.loadStoredPreferences()
    this.calculateSidebarWidth(this.sidebarLevelValue)
    this.updateToggleButtons()
    this.updateFolderVisibility()
    this.updateEmptyToggleUI()
    this.updateEmptyItems()
    this.setupScrollPreservation()
    if (this.isAdminSidebar()) {
      this.boundHandleAdminFrameLoad = this.handleAdminFrameLoad.bind(this)
      document.addEventListener('turbo:frame-load', this.boundHandleAdminFrameLoad)
      this.syncAdminSelection()
      this.configureAdminForms()
    }
  }

  disconnect() {
    this.cleanupScrollPreservation()
    if (this.boundHandleAdminFrameLoad) {
      document.removeEventListener('turbo:frame-load', this.boundHandleAdminFrameLoad)
      this.boundHandleAdminFrameLoad = null
    }
  }

  setupScrollPreservation() {
    if (!this.scrollKeyValue || !this.hasSidebarTarget) return

    this.scrollableNav = this.sidebarTarget.querySelector('nav')
    if (!this.scrollableNav) return

    this.restoreScrollPosition(this.scrollableNav, { consume: true })

    this.beforeRenderHandler = (event) => {
      this.saveScrollPosition()
      this.restoreScrollOnIncomingBody(event.detail?.newBody)
    }
    this.pageHideHandler = () => this.saveScrollPosition()

    document.addEventListener('turbo:before-render', this.beforeRenderHandler)
    window.addEventListener('pagehide', this.pageHideHandler)
  }

  saveScrollPosition() {
    if (!this.scrollableNav) return

    try {
      sessionStorage.setItem(this.scrollStorageKey(), this.scrollableNav.scrollTop.toString())
    } catch (_e) { }
  }

  restoreScrollPosition(nav, { consume } = {}) {
    if (!nav) return

    try {
      const savedPosition = sessionStorage.getItem(this.scrollStorageKey())
      if (savedPosition == null) return

      nav.scrollTop = parseInt(savedPosition, 10)
      if (consume) sessionStorage.removeItem(this.scrollStorageKey())
    } catch (_e) { }
  }

  restoreScrollOnIncomingBody(newBody) {
    if (!newBody) return

    const incomingRoot = newBody.querySelector("[data-sidebar-scroll-key-value]")
    const incomingKey = incomingRoot?.dataset?.sidebarScrollKeyValue
    if (!incomingKey) return

    const incomingNav = incomingRoot.querySelector("nav")
    if (!incomingNav) return

    try {
      const savedPosition = sessionStorage.getItem(`huduSidebarScrollPosition:${incomingKey}`)
      if (savedPosition == null) return

      incomingNav.scrollTop = parseInt(savedPosition, 10)
    } catch (_e) { }
  }

  cleanupScrollPreservation() {
    if (this.beforeRenderHandler) {
      document.removeEventListener('turbo:before-render', this.beforeRenderHandler)
      this.beforeRenderHandler = null
    }
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler)
      this.pageHideHandler = null
    }
  }

  scrollStorageKey() {
    return `huduSidebarScrollPosition:${this.scrollKeyValue}`
  }

  markSelected(event) {
    if (!this.isAdminSidebar()) return

    const item = event.currentTarget.closest('.sidebar__item')
    if (!item) return

    this.sidebarItemTargets.forEach((sidebarItem) => {
      sidebarItem.classList.remove('selected')
    })

    item.classList.add('selected')
  }

  syncAdminSelection(event) {
    if (event && event.target?.id !== 'admin_content') return
    if (!this.isAdminSidebar()) return

    const currentLocation = window.location.pathname + window.location.search
    const matches = []

    this.sidebarItemTargets.forEach((item) => {
      const link = item.querySelector('.sidebar__link')
      if (!link) return

      const linkUrl = new URL(link.href, window.location.origin)
      const linkLocation = linkUrl.pathname + linkUrl.search
      if (linkLocation === currentLocation) {
        matches.push(item)
      }
    })

    if (matches.length === 0) return

    this.sidebarItemTargets.forEach((item) => {
      item.classList.toggle('selected', matches.includes(item))
    })
  }

  isAdminSidebar() {
    return this.hasSidebarTarget && this.sidebarTarget.classList.contains('sidebar--admin')
  }

  handleAdminFrameLoad(event) {
    if (event.target?.id !== 'admin_content') return

    this.syncAdminSelection(event)
    this.configureAdminForms()

    // Turbo frame navigations never reset window scroll, even when promoted
    // with turbo-action="advance" (hotwired/turbo#778). Before the admin area
    // moved into the admin_content frame these were full page visits that
    // always landed at the top, so restore that behavior here.
    window.scrollTo(0, 0)
  }

  configureAdminForms() {
    if (!this.isAdminSidebar()) return

    document.querySelectorAll('#admin_content form').forEach((form) => {
      const method = (form.getAttribute('method') || 'get').toLowerCase()

      if (method === 'get') return
      if (form.dataset.turbo === 'false') return
      if (form.dataset.turboFrame) return

      form.dataset.turboFrame = '_top'
    })
  }

  isFolderHidden(folderId) {
    if (this.sidebarLevelValue == 1) {
      return false
    }
    return this.hidesValue.includes(folderId)
  }

  toggleFolder(event) {
    event.preventDefault()
    const folderId = event.currentTarget.dataset.folderId

    if (this.isFolderHidden(folderId)) {
      this.hidesValue = this.hidesValue.filter(id => id !== folderId)
    } else {
      this.hidesValue = [...this.hidesValue, folderId]
    }

    localStorage.setItem('huduSidebarFolderHides', JSON.stringify(this.hidesValue))
    this.updateFolderVisibility()
  }

  updateFolderVisibility() {
    this.folderContentTargets.forEach(content => {
      const folderId = content.dataset.folderId
      const folderIcon = this.folderIconTargets.find(icon =>
        icon.dataset.folderId === folderId
      )

      if (this.isFolderHidden(folderId)) {
        content.style.display = 'none'
        folderIcon.classList.remove('fa-chevron-down')
        folderIcon.classList.add('fa-chevron-right')
      } else {
        content.style.display = ''
        folderIcon.classList.remove('fa-chevron-right')
        folderIcon.classList.add('fa-chevron-down')
      }
    })
  }

  sidebarLevelValueChanged() {
    this.updateToggleButtons()
    this.updateFolderVisibility()
  }

  updateToggleButtons() {
    this.toggleButtonTargets.forEach(button => {
      const showAtLevel = parseInt(button.dataset.showAtLevel)
      button.style.display = this.sidebarLevelValue === showAtLevel ? '' : 'none'
    })
  }

  loadStoredPreferences() {
    // Load folder hides
    try {
      const storedHides = localStorage.getItem('huduSidebarFolderHides')
      if (storedHides) {
        this.hidesValue = JSON.parse(storedHides)
      }
    } catch(e) { }

    // Load hide empty preference
    if (!this.hasNoHideBlankValue) {
      try {
        if (localStorage.getItem('huduHideEmptySidebarAssets')) {
          this.hideEmptyValue = true
        }
      } catch(e) { }
    }

    // Load sidebar level
    try {
      const level = localStorage.getItem('huduSidebarResizeLevel')
      if (level) {
        this.sidebarLevelValue = parseInt(level)
        this.calculateSidebarWidth(this.sidebarLevelValue)
      }
    } catch(e) { }
  }

  resizeSidebar(event) {
    event.preventDefault()
    const level = parseInt(event.currentTarget.dataset.level)
    this.sidebarLevelValue = level
    localStorage.setItem('huduSidebarResizeLevel', level)
    this.calculateSidebarWidth(level)
  }

  toggleEmpty(event) {
    event.preventDefault()
    this.hideEmptyValue = !this.hideEmptyValue

    if (this.hideEmptyValue) {
      localStorage.setItem('huduHideEmptySidebarAssets', true)
    } else {
      localStorage.removeItem('huduHideEmptySidebarAssets')
    }

    this.updateEmptyToggleUI()
    this.updateEmptyItems()
  }

  updateEmptyToggleUI() {
    if (!this.hasEmptyToggleTarget) return

    const icon = this.emptyIconTarget
    const text = this.emptyTextTarget

    if (this.hideEmptyValue) {
      icon.classList.remove('fa-eye-slash')
      icon.classList.add('fa-eye')
      text.textContent = 'Show Empty Items'
    } else {
      icon.classList.remove('fa-eye')
      icon.classList.add('fa-eye-slash')
      text.textContent = 'Hide Empty Items'
    }
  }

  updateEmptyItems() {
    // First, handle individual items
    this.sidebarItemTargets.forEach(item => {
      const countElement = item.querySelector('.count')
      if (!countElement) return

      const count = countElement.textContent
      if (this.hideEmptyValue && count === '0') {
        item.style.display = 'none'
      } else {
        item.style.display = ''
      }
    })

    // Then, handle categories
    this.categoryTitleTargets.forEach(categoryTitle => {
      const folderId = categoryTitle.dataset.folderId
      const folderContent = this.folderContentTargets.find(content =>
        content.dataset.folderId === folderId
      )

      if (!folderContent) return

      // Count visible items in this category
      const visibleItems = Array.from(folderContent.querySelectorAll('.sidebar__item'))
        .filter(item => item.style.display !== 'none')
        .length

      // Hide category if it has no visible items
      categoryTitle.style.display = visibleItems > 0 ? '' : 'none'

      // Update folder content visibility based on both visible items and folder state
      if (visibleItems === 0) {
        folderContent.style.display = 'none'
      } else {
        // Only show if the folder isn't manually collapsed
        folderContent.style.display = this.isFolderHidden(folderId) ? 'none' : ''
      }
    })

    // Finally, update folder icons to match their state
    this.updateFolderVisibility()
  }

  calculateSidebarWidth(level) {
    if (!this.hasSidebarTarget) return

    if (level == 1) {
      this.sidebarTarget.classList.add("sidebar--skinny")
      document.documentElement.style.setProperty('--sidebarwidth', '85px')
    } else if (level == 2) {
      this.sidebarTarget.classList.remove("sidebar--skinny")
      document.documentElement.style.setProperty('--sidebarwidth', '380px')
    } else {
      this.sidebarTarget.classList.remove("sidebar--skinny")
      document.documentElement.style.setProperty('--sidebarwidth', '235px')
    }
  }
}
